import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import {
  T,
  OPERATIONAL_STATUS,
  formatClock,
  formatRelative,
  formatSince,
} from "../../constants";
import Plate from "../../components/Plate";
import VipBadge from "../../components/VipBadge";
import { ATTENTION, ON_SITE_TABS, REQUESTED_STATUSES } from "../onSiteTabs";
import ColumnPanel from "./ColumnPanel";
import { useRowStyles } from "./boardRows";

const useStyles = makeStyles({
  tabs: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    margin: 0,
    padding: "2px 20px 10px",
    listStyle: "none",
  },
  tab: {
    border: "none",
    background: "transparent",
    color: T.mutedFg,
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    "&:hover": { color: T.fg },
    '&[aria-selected="true"]': {
      background: T.accent,
      color: T.accentFg,
      fontWeight: 600,
    },
  },
  count: {
    fontVariantNumeric: "tabular-nums",
    minWidth: 18,
    textAlign: "center",
    borderRadius: 999,
    background: T.muted,
    padding: "0 6px",
    fontSize: 11,
    color: T.mutedFg,
    lineHeight: "18px",
  },
  countSelected: { background: T.primary, color: T.primaryFg },
  unassigned: { fontSize: 11, color: T.primary, fontWeight: 600, marginLeft: 4 },
});

const ATTENTION_LABEL = {
  [ATTENTION.DEPARTURE]: "Past expected departure",
  [ATTENTION.WINDOW]: "Booking window passed",
  [ATTENTION.LATE]: "Pickup running late",
  [ATTENTION.UNPAID]: "Balance due before handover",
};

/* Where the car actually is, as opposed to where its guest card pointed it. */
export function whereText(car) {
  if (!car.parkedLotName) {
    return car.lotName ? `${car.lotName} · to be confirmed` : "Location to be confirmed";
  }

  if (car.parkedLotType === "OFFSITE") {
    const ticket = car.parkedTicket ? `ticket ${car.parkedTicket}` : "ticket to be confirmed";
    return `${car.parkedLotName} · ${ticket}${car.bay ? ` · ${car.bay}` : ""}`;
  }

  return car.bay ? `${car.parkedLotName} · Bay ${car.bay}` : car.parkedLotName;
}

/* The one line of timing that matters for this car, right now. */
export function eyebrowFor(car, hydrated) {
  if (car.pickedByName) return `Runner · ${car.pickedByName.split(" ")[0]}`;
  if (car.parkedByName && car.status === "PARKING") {
    return `Runner · ${car.parkedByName.split(" ")[0]}`;
  }
  if (REQUESTED_STATUSES.includes(car.status) && car.requestedAt) {
    return `Pickup ${hydrated ? formatClock(car.requestedAt) : ""}`.trim();
  }
  if (car.expectedDeparture) {
    return `Leaves ${hydrated ? formatClock(car.expectedDeparture) : ""}`.trim();
  }

  return "Departure not set";
}

/*
 * Where this car is in the flow, and what is holding it there. Two facts read
 * as one sentence, because a status alone does not say why nobody has moved.
 */
export function statusLine(car) {
  const parts = [OPERATIONAL_STATUS[car.status] ?? car.status];

  if (car.status === "REQUESTED" && !car.pickedBy) parts.push("Unassigned");
  if (car.status === "READY" && car.paymentRequired !== false && !car.paid) {
    parts.push("Unpaid");
  }

  return parts.join(" · ");
}

function rightText(tab, car, hydrated) {
  if (!hydrated) return "-";

  if (tab === "unknown") {
    return car.parkedAt ? `parked ${formatSince(car.parkedAt)} ago` : "-";
  }

  if (tab === "attention") {
    const at = REQUESTED_STATUSES.includes(car.status)
      ? car.requestedAt
      : car.expectedDeparture ?? car.checkOut;
    return at ? `${formatSince(at)} ago` : "-";
  }

  const at = REQUESTED_STATUSES.includes(car.status)
    ? car.requestedAt
    : car.expectedDeparture;

  return at ? formatRelative(at) : "-";
}

export default function OnSiteColumn({
  tabs,
  unassigned = 0,
  hydrated,
  onOpenCar,
}) {
  const s = useStyles();
  const r = useRowStyles();
  const [active, setActive] = useState("requested");

  const rows = tabs[active] ?? [];

  return (
    <ColumnPanel
      title="On site"
      hideCount
      toolbar={
        <ul className={s.tabs} role="tablist" aria-label="On-site status">
          {ON_SITE_TABS.map((tab) => {
            const selected = tab.id === active;

            return (
              <li key={tab.id}>
                <button
                  type="button"
                  role="tab"
                  className={s.tab}
                  aria-selected={selected}
                  onClick={() => setActive(tab.id)}
                >
                  {tab.label}
                  <span
                    className={`${s.count} ${selected ? s.countSelected : ""}`}
                  >
                    {(tabs[tab.id] ?? []).length}
                  </span>
                  {tab.id === "requested" && unassigned > 0 && (
                    <span
                      className={s.unassigned}
                      aria-label={`${unassigned} unassigned`}
                    >
                      {unassigned} unassigned
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      }
      empty={`Nothing in ${
        ON_SITE_TABS.find((tab) => tab.id === active)?.label.toLowerCase()
      }.`}
      itemCount={rows.length}
    >
      <ul className={r.list} role="tabpanel">
        {rows.map((car) => (
          <li key={car.id} data-row>
            <button
              type="button"
              className={r.row}
              onClick={() => onOpenCar(car)}
            >
              <Plate size={11}>{car.plate}</Plate>

              <div className={r.main}>
                <div className={r.line}>
                  <span className={r.name}>
                    {car.guestName || "Unknown vehicle"}
                  </span>
                  {car.vip && <VipBadge />}
                </div>
                <div className={r.meta}>{whereText(car)}</div>
                {car.attentionReasons?.length > 0 && (
                  <div className={r.attention}>
                    {ATTENTION_LABEL[car.attentionReasons[0]]}
                  </div>
                )}
              </div>

              <div className={r.right}>
                <span className={r.eyebrow}>{eyebrowFor(car, hydrated)}</span>
                <span className={r.status}>{statusLine(car)}</span>
                <span className={r.note}>{rightText(active, car, hydrated)}</span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </ColumnPanel>
  );
}
